/**
 * Applying an engine suggestion to the session (spec `observer-autogen-review-walkthrough`, R4.2,
 * R4.5, R5.1). Pure: the host records the undo step, sets the result and marks the plan dirty, so an
 * accepted region reaches the plan — and only the plan — through the same session state a drawn
 * region does. Nothing here, or downstream of it, writes `twin_sense_observer`: Go Live is the only
 * writer (row 24).
 */
import type { ObserverTrackSide } from '@repo/frontend-types';
import type { Point, Polygon } from '../../../components/PolygonPlotter2/PolygonPlotter2';
import { linkRegion, observerRefid, type ObserverStateByRefid } from '../observerSessionState';

/** The prefix of the polygon id a suggestion takes. */
export const SUGGESTED_POLYGON_ID = 'suggested';

/**
 * A fresh id per acceptance. ⚠ Never the bare prefix: the replacement drops the machine's regions
 * here BEFORE `linkRegion` checks for a clash, so a second accept after a save would mint the very
 * refid the engine's answer already knows, and the machine would never read as accepted (Copilot,
 * #2156).
 */
const freshSuggestedId = (): string =>
  `${SUGGESTED_POLYGON_ID}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/**
 * The session after `targetRefid` takes `points` as its region on `endpointId`, replacing whatever
 * regions it already had on THAT camera (its regions on other cameras are untouched).
 */
export const applySuggestion = (
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
  targetRefid: string,
  points: Point[],
  trackSide: ObserverTrackSide,
  endpointId: number,
): {
  polygonsByEntity: Record<string, Polygon[]>;
  observerState: ObserverStateByRefid;
  observerRefid: string;
} => {
  const before = polygonsByEntity[targetRefid] ?? [];
  const onCamera = (p: Polygon) =>
    p.isComplete && observerState[observerRefid(targetRefid, p.id)]?.endpointId === endpointId;
  const kept = before.filter(p => !onCamera(p));
  const nextState: ObserverStateByRefid = { ...observerState };
  for (const p of before) {
    if (onCamera(p)) delete nextState[observerRefid(targetRefid, p.id)];
  }
  return linkRegion(
    { ...polygonsByEntity, [targetRefid]: kept },
    nextState,
    targetRefid,
    { id: freshSuggestedId(), points: points.map(p => ({ x: p.x, y: p.y })), isComplete: true },
    endpointId,
    trackSide,
  );
};

const pointsKey = (points: readonly Point[]): string => points.map(p => `${p.x},${p.y}`).join(';');

/**
 * The observers that still have a completed region behind them. Clear Observer removes several
 * regions but only the selected one's metadata, so `observerState` alone can hold orphans that
 * would read as accepted, or hide a deletion (Copilot, #2156).
 */
export const liveObserverState = (
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
): ObserverStateByRefid => {
  const out: ObserverStateByRefid = {};
  for (const [target, polygons] of Object.entries(polygonsByEntity)) {
    for (const p of polygons) {
      const refid = observerRefid(target, p.id);
      if (p.isComplete && observerState[refid]) out[refid] = observerState[refid];
    }
  }
  return out;
};

/**
 * Suggestions whose exact region sits on ANOTHER machine on `endpointId` — taken there by Wrong
 * machine. Read from the session, so Undo hands them back.
 */
export const takenProposals = (
  proposals: readonly { refid: string; targetRefid: string | null; points: readonly Point[] }[],
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
  endpointId: number | null,
): Set<string> => {
  const out = new Set<string>();
  if (endpointId == null) return out;
  const regionsOnCamera = new Map<string, Set<string>>();
  for (const [target, polygons] of Object.entries(polygonsByEntity)) {
    for (const p of polygons) {
      if (!p.isComplete) continue;
      if (observerState[observerRefid(target, p.id)]?.endpointId !== endpointId) continue;
      const key = pointsKey(p.points);
      const owners = regionsOnCamera.get(key) ?? new Set<string>();
      owners.add(target);
      regionsOnCamera.set(key, owners);
    }
  }
  for (const proposal of proposals) {
    const owners = regionsOnCamera.get(pointsKey(proposal.points));
    if (owners && Array.from(owners).some(t => t !== proposal.targetRefid)) out.add(proposal.refid);
  }
  return out;
};

/**
 * Machines on `endpointId` that have an observer the engine's answer didn't know about — drawn or
 * accepted since the read. Derived from the session itself, so an Undo puts a machine straight back.
 */
export const targetsWithNewObservers = (
  observerState: ObserverStateByRefid,
  endpointId: number | null,
  knownRefidsByTarget: ReadonlyMap<string, ReadonlySet<string>>,
): Set<string> => {
  const out = new Set<string>();
  if (endpointId == null) return out;
  for (const [refid, state] of Object.entries(observerState)) {
    if (state.endpointId !== endpointId) continue;
    const target = refid.slice(0, refid.indexOf(':'));
    const known = knownRefidsByTarget.get(target);
    if (known && !known.has(refid)) out.add(target);
  }
  return out;
};
